"use client";

import { CircleAlert, Languages, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Dialog } from "@nitro/ui";
import {
  browserLanguageRuntime,
  LANGUAGE_OPTIONS,
  LANGUAGE_SOURCE,
  languageRuntimeErrorKind,
  normalizeLanguageCode,
  restoreStoredLanguage,
  runLanguageSelection,
} from "../lib/languageRuntime";

const DEFAULT_CONTAINER_ID = "google_translate_element";

export function LanguageTranslator({ containerId = DEFAULT_CONTAINER_ID, runtime = browserLanguageRuntime }) {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(LANGUAGE_SOURCE);
  const [pendingLanguage, setPendingLanguage] = useState("");
  const [runtimeError, setRuntimeError] = useState(null);
  const [retryPersistenceLanguage, setRetryPersistenceLanguage] = useState("");
  const generationRef = useRef(0);
  const lifecycleAbortRef = useRef(null);
  const restoreAbortRef = useRef(null);
  const restoreRetryRef = useRef(null);
  const selectionAbortRef = useRef(null);
  const providerPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    const generation = ++generationRef.current;
    const lifecycleController = new AbortController();
    const restoreController = new AbortController();
    lifecycleAbortRef.current = lifecycleController;
    restoreAbortRef.current = restoreController;
    selectionAbortRef.current?.abort();
    setPendingLanguage("");
    setRuntimeError(null);
    setRetryPersistenceLanguage("");

    const current = () => generationRef.current === generation && !lifecycleController.signal.aborted;
    const linkRestoreAbort = () => restoreController.abort(lifecycleController.signal.reason);
    lifecycleController.signal.addEventListener("abort", linkRestoreAbort, { once: true });

    let unsubscribe = () => {};
    try {
      unsubscribe = runtime.subscribe((nextLanguage) => {
        if (current()) setLanguage(normalizeLanguageCode(nextLanguage));
      });
    } catch (error) {
      if (current()) showRuntimeError(error, setOpen, setRuntimeError);
    }

    const providerPromise = Promise.resolve().then(() => runtime.start({
      containerId,
      languages: LANGUAGE_OPTIONS.map(({ code }) => code),
      signal: lifecycleController.signal,
    }));
    providerPromiseRef.current = providerPromise;
    restoreStoredLanguage({
      onApplied: (appliedLanguage) => {
        if (current()) setLanguage(appliedLanguage);
      },
      providerReady: providerPromise,
      runtime,
      signal: restoreController.signal,
    })
      .catch((error) => {
        if (current() && !isAbortError(error)) showRuntimeError(error, setOpen, setRuntimeError);
      });

    return () => {
      lifecycleController.abort();
      restoreController.abort();
      restoreAbortRef.current?.abort();
      restoreRetryRef.current = null;
      selectionAbortRef.current?.abort();
      lifecycleController.signal.removeEventListener("abort", linkRestoreAbort);
      unsubscribe();
      if (generationRef.current === generation) generationRef.current += 1;
    };
  }, [containerId, runtime]);

  async function selectLanguage(nextLanguage) {
    const code = normalizeLanguageCode(nextLanguage);
    const generation = generationRef.current;
    restoreAbortRef.current?.abort();
    selectionAbortRef.current?.abort();
    const controller = new AbortController();
    selectionAbortRef.current = controller;
    setPendingLanguage(code);
    setRuntimeError(null);
    setRetryPersistenceLanguage("");

    try {
      const result = await runLanguageSelection({
        code,
        onApplied: (appliedLanguage) => setLanguage(appliedLanguage),
        providerReady: providerPromiseRef.current,
        runtime,
        signal: controller.signal,
      });
      throwIfSelectionStale(controller.signal, generationRef, generation);
      setPendingLanguage("");
      if (result.persisted) {
        setOpen(false);
      } else {
        setRetryPersistenceLanguage(code);
        setRuntimeError({
          kind: "storage",
          title: "Preferencia no guardada",
          message: "El idioma se aplicó, pero no pudimos guardar la preferencia para tu próxima visita.",
        });
        setOpen(true);
      }
    } catch (error) {
      if (controller.signal.aborted || generationRef.current !== generation || isAbortError(error)) return;
      setPendingLanguage("");
      showRuntimeError(error, setOpen, setRuntimeError);
    }
  }

  async function retryRuntime() {
    if (retryPersistenceLanguage) {
      try {
        runtime.write(retryPersistenceLanguage);
        setRetryPersistenceLanguage("");
        setRuntimeError(null);
        setOpen(false);
      } catch (error) {
        showRuntimeError(error, setOpen, setRuntimeError);
      }
      return;
    }
    selectionAbortRef.current?.abort();
    restoreAbortRef.current?.abort();
    const controller = new AbortController();
    const token = Object.freeze({ generation: generationRef.current });
    restoreAbortRef.current = controller;
    restoreRetryRef.current = token;
    const current = () => restoreRetryRef.current === token
      && generationRef.current === token.generation
      && !controller.signal.aborted
      && !lifecycleAbortRef.current?.signal.aborted;
    const providerPromise = Promise.resolve().then(() => runtime.start({
      containerId,
      languages: LANGUAGE_OPTIONS.map(({ code }) => code),
      signal: controller.signal,
    }));
    providerPromiseRef.current = providerPromise;
    try {
      await restoreStoredLanguage({
        onApplied: (appliedLanguage) => {
          if (current()) setLanguage(appliedLanguage);
        },
        providerReady: providerPromise,
        runtime,
        signal: controller.signal,
      });
      if (!current()) return;
      restoreRetryRef.current = null;
      setRuntimeError(null);
      setOpen(false);
    } catch (error) {
      if (restoreRetryRef.current === token) restoreRetryRef.current = null;
      if (!controller.signal.aborted && generationRef.current === token.generation && !isAbortError(error)) {
        showRuntimeError(error, setOpen, setRuntimeError);
      }
    }
  }

  function openLanguageDialog() {
    cancelRestoreRetry();
    setOpen(true);
  }

  function closeLanguageDialog() {
    cancelRestoreRetry();
    setOpen(false);
  }

  function cancelRestoreRetry() {
    if (!restoreRetryRef.current) return;
    restoreRetryRef.current = null;
    restoreAbortRef.current?.abort();
  }

  const activeLanguage = LANGUAGE_OPTIONS.find((item) => item.code === language);
  const pendingLabel = pendingLanguage === LANGUAGE_SOURCE
    ? "Español"
    : LANGUAGE_OPTIONS.find((item) => item.code === pendingLanguage)?.label;

  return (
    <>
      <button aria-label={`Cambiar idioma: ${activeLanguage?.label || "Español"}`} aria-haspopup="dialog" aria-expanded={open} className="nitro-language-button notranslate" onClick={openLanguageDialog} title={`Idioma: ${activeLanguage?.label || "Español"}`} translate="no" type="button">
        <Languages size={16} aria-hidden="true" />
        <span>{activeLanguage?.label || "Idioma"}</span>
      </button>

      <Dialog open={open} onClose={closeLanguageDialog} title="Idioma" description="Selecciona el idioma para traducir el portal automáticamente." closeLabel="Cerrar selector de idioma" size="sm" className="notranslate" translate="no">
        {runtimeError ? (
          <Alert icon={<CircleAlert size={18} />} tone="danger">
            <div>
              <strong>{runtimeError.title}</strong>
              <p>{runtimeError.message}</p>
              <Button iconStart={<RefreshCw size={16} />} onClick={retryRuntime} style={{ minHeight: 44 }} type="button" variant="secondary">
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : null}
        {pendingLabel ? <p aria-live="polite" role="status">Cambiando a {pendingLabel}...</p> : null}
        <div aria-busy={Boolean(pendingLanguage)} className="nitro-language-list">
          <button aria-pressed={language === LANGUAGE_SOURCE} className={language === LANGUAGE_SOURCE ? "is-active" : ""} onClick={() => selectLanguage(LANGUAGE_SOURCE)} type="button">
            <Languages size={16} />
            Español
          </button>
          {LANGUAGE_OPTIONS.map((item) => (
            <button aria-pressed={language === item.code} className={language === item.code ? "is-active" : ""} key={item.code} onClick={() => selectLanguage(item.code)} type="button">
              <Languages size={16} />
              {item.label}
            </button>
          ))}
        </div>
      </Dialog>

      <div className="nitro-google-translate notranslate" id={containerId} translate="no" />
    </>
  );
}

export function TranslationNotice({ runtime = browserLanguageRuntime }) {
  const [language, setLanguage] = useState(LANGUAGE_SOURCE);

  useEffect(() => {
    let active = true;
    try {
      setLanguage(normalizeLanguageCode(runtime.readApplied()));
    } catch {
      setLanguage(LANGUAGE_SOURCE);
    }

    let unsubscribe = () => {};
    try {
      unsubscribe = runtime.subscribe((nextLanguage) => {
        if (active) setLanguage(normalizeLanguageCode(nextLanguage));
      });
    } catch {
      /* Selector owns the accessible runtime error. */
    }
    return () => {
      active = false;
      unsubscribe();
    };
  }, [runtime]);

  const activeLanguage = LANGUAGE_OPTIONS.find((item) => item.code === language);
  if (!activeLanguage) return null;

  return (
    <div className="nitro-translation-note notranslate" translate="no">
      <CircleAlert size={15} />
      <span>{activeLanguage.note}</span>
    </div>
  );
}

function showRuntimeError(error, setOpen, setRuntimeError) {
  const kind = languageRuntimeErrorKind(error);
  setRuntimeError({
    kind,
    title: kind === "storage" ? "Preferencia no disponible" : "Traducción no disponible",
    message: error?.message || "No pudimos completar el cambio de idioma.",
  });
  setOpen(true);
}

function throwIfSelectionStale(signal, generationRef, generation) {
  if (!signal.aborted && generationRef.current === generation) return;
  const error = signal.reason instanceof Error ? signal.reason : new Error("Selección de idioma cancelada.");
  error.name = "AbortError";
  throw error;
}

function isAbortError(error) {
  return error?.name === "AbortError";
}
